import Button from '@material-ui/core/Button'
import Chip from '@material-ui/core/Chip'
import { withStyles, WithStyles } from '@material-ui/core/styles'
import createStyles from '@material-ui/core/styles/createStyles'
import { Theme } from '@material-ui/core/styles/createTheme'
import { SvgIconProps } from '@material-ui/core/SvgIcon'
import Tooltip from '@material-ui/core/Tooltip'
import Battery20 from '@material-ui/icons/Battery20'
import Battery30 from '@material-ui/icons/Battery30'
import Battery50 from '@material-ui/icons/Battery50'
import Battery60 from '@material-ui/icons/Battery60'
import Battery80 from '@material-ui/icons/Battery80'
import Battery90 from '@material-ui/icons/Battery90'
import BatteryAlert from '@material-ui/icons/BatteryAlert'
import BatteryFull from '@material-ui/icons/BatteryFull'
import BatteryUnknown from '@material-ui/icons/BatteryUnknown'
import Place from '@material-ui/icons/Place'
import classNames from 'classnames'
import upperFirst from 'lodash/upperFirst'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { Provider, RetrieveVehicle } from 'src/api/gen/api'
import { enumsTranslationKeys } from 'src/commons/i18nEnums'
import { TemporalNow, humanizedDuration, stringToZonedDateTime } from 'src/commons/temporalHelpers'
import { renderVehicleIcon } from 'src/components/Devices'

import { IDeviceSupervisionMap } from './types'

export const styles = (theme: Theme) =>
  createStyles({
    container: {
      display: 'flex',
      flexDirection: 'column',
    },
    root: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      minWidth: 320,
      padding: '4px',
    },
    reportButton: {
      margin: '4px 8px 8px',
      width: 'auto',
    },
    media: {
      textAlign: 'center',
      minHeight: 80,
      minWidth: 80,
      marginLeft: theme.spacing(),
      backgroundSize: 'contain',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
    },
    header: {
      width: '100%',
      maxWidth: '200px',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      fontSize: '20px',
      lineHeight: '30px',
      color: theme.palette.grey[800],
      cursor: 'pointer',
    },
    tooltip: {
      fontSize: 13,
    },
    vehicleHeading: {
      maxWidth: '17ch', // The ellipsis takes some space, this seems to work best
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    vehicleTitle: {
      fontSize: '18px',
      lineHeight: '20px',
      fontWeight: 'bold',
    },
    vehicleSubTitle: {
      fontSize: '15px',
      lineHeight: '20px',
      color: theme.palette.grey[500],
    },
    vehicleInfoContainer: {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(3),
      minWidth: '220px',
      display: 'flex',
      flexDirection: 'column',
    },
    row: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    column: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },
    vehicleBatContainer: {
      display: 'flex',
      alignItems: 'center',
      fontWeight: 'bold',
      color: theme.palette.grey[500],
    },
    vertLine: {
      height: 100,
      width: 2,
      background: '#e8e7e7',
      marginLeft: theme.spacing(2),
      marginRight: theme.spacing(2),
    },
    detailsButton: {
      width: '100%',
    },
    infoRowTitle: {
      fontSize: 15,
    },
    infoRowValue: {
      fontSize: 15,
      fontWeight: 'bold',
    },
    chip: {
      padding: 5,
      color: theme.palette.grey[500],
      borderRadius: 5,
      fontSize: 15,
    },
    statusChip: {
      padding: theme.spacing(),
      borderRadius: 5,
      fontSize: 11,
    },
    lastTelemetryIco: {
      fontSize: 18,
    },
    lastTelemetryTxt: {
      color: 'inherit',
    },
    vehicleStatus: {
      fontSize: 15,
      color: theme.palette.grey[500],
    },
    vehicleTelemetry: {
      color: theme.palette.grey[500],
    },
    vehicleIcon: {
      position: 'relative',
      right: '10px',
      top: '10px',
    },
    batteryIcon: {
      marginLeft: 0,
    },
    batteryLabel: {
      fontSize: 14,
      paddingLeft: 6,
      paddingRight: 6,
      minWidth: 54,
      justifyContent: 'center',
    },
  })

interface IProps extends WithStyles<typeof styles> {
  device: IDeviceSupervisionMap | RetrieveVehicle
  onDeviceClick?: (deviceID: string) => void
  /** When set, show a button to report a Smart Patrol offense for this vehicle. */
  onReportOffense?: () => void
  provider?: Provider
  showBattery?: boolean
  showTelemetry?: boolean
}

const VehiclePopup = ({
  classes,
  provider,
  device,
  onDeviceClick,
  onReportOffense,
  showTelemetry = true,
  showBattery = true,
}: IProps) => {
  const now = TemporalNow()
  const { t } = useTranslation()
  const updateTime = stringToZonedDateTime(device.last_known_timestamp.toString())

  const getBatteryIcon = (): React.ComponentType<SvgIconProps> => {
    if (device.last_known_battery_pct) {
      const level = device.last_known_battery_pct * 100
      if (level <= 0) {
        return BatteryAlert
      }
      if (level >= 0 && level < 20) {
        return Battery20
      }
      if (level >= 20 && level < 30) {
        return Battery30
      }
      if (level >= 30 && level < 50) {
        return Battery50
      }
      if (level >= 50 && level < 60) {
        return Battery60
      }
      if (level >= 60 && level < 80) {
        return Battery80
      }
      if (level >= 80 && level < 90) {
        return Battery90
      }
      if (level >= 90 && level <= 100) {
        return BatteryFull
      }
    }

    return BatteryUnknown
  }
  const BatteryIcon = getBatteryIcon()
  const deviceIdUpper = device.identification_number.toUpperCase()
  return (
    <div className={classes.container}>
      <div className={classes.root}>
        <div className={classes.column}>
          <div
            style={{
              backgroundImage: `url(${provider ? provider.logo : '/img/img-placeholder.svg'})`,
            }}
            className={classes.media}
          />
        </div>
        <div className={classes.vertLine} />
        <div style={{ width: '100%' }}>
          <div className={classes.row}>
            <div className={classes.header} onClick={() => (onDeviceClick ? onDeviceClick(device.id) : null)}>
              <Tooltip
                classes={{ tooltip: classes.tooltip }}
                title={device.provider_name}
                aria-label={device.provider_name}
              >
                <div className={classNames(classes.vehicleHeading, classes.vehicleTitle)}>{device.provider_name}</div>
              </Tooltip>
              <div className={classes.row} style={{ height: 5 }} />
              <Tooltip classes={{ tooltip: classes.tooltip }} title={deviceIdUpper} aria-label={deviceIdUpper}>
                <div className={classNames(classes.vehicleHeading, classes.vehicleSubTitle)}>{deviceIdUpper}</div>
              </Tooltip>
              <div className={classes.row} style={{ height: 5 }} />
              <div className={classNames(classes.row, classes.vehicleStatus)}>
                {upperFirst(t(enumsTranslationKeys.VehicleStates[device.last_known_status]))}
              </div>
            </div>
            {renderVehicleIcon(device.type, classes.vehicleIcon, 40)}
          </div>
          <div className={classes.row} style={{ height: 10 }} />
          <div className={classes.row}>
            {showTelemetry && (
              <div className={classNames(classes.row, classes.vehicleTelemetry)}>
                <Place className={classes.lastTelemetryIco} />
                <div className={classes.lastTelemetryTxt}>
                  {device.last_known_timestamp ? humanizedDuration(updateTime.since(now)) : ' - '}
                </div>
              </div>
            )}
            {showBattery && (
              <div className={classes.vehicleBatContainer}>
                <Chip
                  icon={<BatteryIcon className={classes.batteryIcon} />}
                  label={device.last_known_battery_pct ? `${Math.round(device.last_known_battery_pct * 100)}%` : '-'}
                  classes={{ label: classes.batteryLabel }}
                  className={classes.chip}
                  variant="outlined"
                />
              </div>
            )}
          </div>
        </div>
      </div>
      {onReportOffense && (
        <Button
          className={classes.reportButton}
          variant="contained"
          color="primary"
          size="small"
          fullWidth
          onClick={onReportOffense}
        >
          {t('smart_patrol.create_offense.title')}
        </Button>
      )}
    </div>
  )
}

export default withStyles(styles)(VehiclePopup)
